import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { labelTextColor } from "@/lib/labels";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";

const PRESETS = [
  { name: "Blau", hex: "#3B82F6" },
  { name: "Violett", hex: "#8B5CF6" },
  { name: "Gelb", hex: "#EAB308" },
  { name: "Grün", hex: "#22C55E" },
  { name: "Dunkelgrün", hex: "#15803D" },
  { name: "Rot", hex: "#EF4444" },
  { name: "Orange", hex: "#F97316" },
  { name: "Pink", hex: "#EC4899" },
  { name: "Cyan", hex: "#06B6D4" },
  { name: "Grau", hex: "#64748B" },
];

const emptyForm = { name: "", color: "#3B82F6", text_color: "#ffffff", border_color: "#3B82F6" };

function Pill({ l }) {
  return (
    <span className="inline-block text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full border"
      style={{ backgroundColor: l.color, color: l.text_color || labelTextColor(l.color), borderColor: l.border_color || l.color }}>
      {l.name || "Vorschau"}
    </span>
  );
}

export default function LabelsSettings() {
  const [labels, setLabels] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const load = () => api.get("/status-labels").then((r) => setLabels(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setEditId(null); setOpen(true); };
  const openEdit = (l) => {
    setForm({ name: l.name, color: l.color, text_color: l.text_color || labelTextColor(l.color), border_color: l.border_color || l.color });
    setEditId(l.id); setOpen(true);
  };

  const pickPreset = (hex) => setForm((f) => ({ ...f, color: hex, border_color: hex, text_color: labelTextColor(hex) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name erforderlich"); return; }
    try {
      if (editId) await api.put(`/status-labels/${editId}`, form);
      else await api.post("/status-labels", form);
      toast.success("Label gespeichert");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Label "${l.name}" löschen? Es wird von allen Artikeln entfernt.`)) return;
    try { await api.delete(`/status-labels/${l.id}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const dropOn = async (targetIdx) => {
    const from = dragIdx;
    setDragIdx(null); setOverIdx(null);
    if (from === null || from === targetIdx) return;
    const next = [...labels];
    const [moved] = next.splice(from, 1);
    next.splice(targetIdx, 0, moved);
    setLabels(next);
    try { await api.post("/status-labels/reorder", { ids: next.map((x) => x.id) }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Status-Labels</h2>
          <p className="text-slate-500 text-sm">Ampelsystem für den Artikelstatus – frei benennbar und farblich anpassbar.</p>
        </div>
        <Button onClick={openNew} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="add-label-button">
          <Plus size={16} className="mr-1.5" /> Neues Label
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-2" data-testid="labels-list">
        {labels.map((l, idx) => (
          <div key={l.id}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(idx); }}
            onDragLeave={() => setOverIdx((v) => (v === idx ? null : v))}
            onDrop={() => dropOn(idx)}
            className={`flex items-center gap-3 rounded-lg px-2 py-1.5 ${overIdx === idx && dragIdx !== null ? "ring-2 ring-blue-400" : ""} ${dragIdx === idx ? "opacity-50" : ""}`}
            data-testid={`label-row-${l.id}`}>
            <span draggable onDragStart={() => setDragIdx(idx)} onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 shrink-0" title="Zum Verschieben ziehen"
              data-testid={`label-drag-${l.id}`}>
              <GripVertical size={16} />
            </span>
            <Pill l={l} />
            <div className="ml-auto flex items-center gap-1">
              <button onClick={() => openEdit(l)} className="text-slate-400 hover:text-slate-700" data-testid={`label-edit-${l.id}`}><Pencil size={16} /></button>
              <button onClick={() => remove(l)} className="text-slate-400 hover:text-red-500" data-testid={`label-delete-${l.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {labels.length === 0 && <div className="text-slate-400 text-sm py-6 text-center">Noch keine Labels angelegt.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="label-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Label bearbeiten" : "Neues Label"}</DialogTitle>
            <DialogDescription>Ändere Name und Farbe des Labels.</DialogDescription>
          </DialogHeader>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="label-name-input" />
          </div>

          <div className="mt-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Farbe</Label>
            <div className="grid grid-cols-5 gap-2 mt-2">
              {PRESETS.map((p) => (
                <button key={p.hex} onClick={() => pickPreset(p.hex)}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-2 transition hover:border-slate-400 ${form.color.toLowerCase() === p.hex.toLowerCase() ? "border-blue-500 ring-2 ring-blue-200" : "border-slate-200"}`}
                  data-testid={`preset-${p.name}`}>
                  <span className="h-6 w-6 rounded-full" style={{ backgroundColor: p.hex }} />
                  <span className="text-xs text-slate-600">{p.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-2">
            <div>
              <Label className="text-xs text-slate-500">Farbe (HEX)</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="h-8 w-8 rounded border border-slate-200 shrink-0" style={{ backgroundColor: form.text_color }} />
                <Input value={form.text_color} onChange={(e) => setForm({ ...form, text_color: e.target.value })} className="mono text-sm" data-testid="label-text-color" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Hintergrund</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="h-8 w-8 rounded border border-slate-200 shrink-0" style={{ backgroundColor: form.color }} />
                <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mono text-sm" data-testid="label-bg-color" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Rahmen</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="h-8 w-8 rounded border border-slate-200 shrink-0" style={{ backgroundColor: form.border_color }} />
                <Input value={form.border_color} onChange={(e) => setForm({ ...form, border_color: e.target.value })} className="mono text-sm" data-testid="label-border-color" />
              </div>
            </div>
          </div>

          <div className="mt-3">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Vorschau</Label>
            <div className="mt-2"><Pill l={form} /></div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} className="bg-blue-700 hover:bg-blue-800" data-testid="label-save-button">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
